import { zodResolver } from '@hookform/resolvers/zod';
import { Divider, Typography } from '@mui/material';
import {
  GetInfluencerQuery,
  useUpdateCreatorProfileMutation,
} from 'generated/graphql';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import { FeatureSettings } from '@client/components/FeatureSettings';
import { TextField } from '@client/new/components/basic';
import { PrimaryButton } from '@client/new/components/button';
import { muiForm } from '@client/new/helpers/forms';
import { useInfluencer } from '@client/new/hooks/use-influencer';

import { SettingsPageCustomDomain } from '../domain/DomainSetup/SettingsPageCustomDomain';
import { CreatorSettings, creatorSettings } from './CreatorAddress.schema';
import {
  hasPremiumFeaturesOTPIds,
  isSubscriptionCreator,
} from '@client/new/common/offering.helpers';

const useAccountForm = (defaultValues: Partial<CreatorSettings> = {}) => {
  return muiForm(
    useForm<CreatorSettings>({
      defaultValues: defaultValues,
      resolver: zodResolver(creatorSettings),
    }),
  );
};

const withData = (Component: typeof CreatorAccount) => () => {
  const { influencer, isLoading, error } = useInfluencer();

  if (isLoading) {
    return null;
  }

  if (error) {
    return <div>{error.message}</div>;
  }

  if (!influencer) {
    return null;
  }

  return <Component creator={influencer} />;
};

function CreatorAccount({
  creator,
}: {
  creator: NonNullable<GetInfluencerQuery['creator']>;
}) {
  const { register, handleSubmit } = useAccountForm(creator as CreatorSettings);
  const [isSaving, setIsSaving] = React.useState(false);

  const [updateCreatorProfileMutation] = useUpdateCreatorProfileMutation();

  const showFeatureSettings =
    isSubscriptionCreator(creator?.offering) ||
    hasPremiumFeaturesOTPIds(creator?.customizations);

  const onSave = handleSubmit((data) => {
    setIsSaving(true);
    try {
      updateCreatorProfileMutation({
        variables: {
          data: {
            id: creator.id,
            ...data,
          },
        },
      });
      toast.success('Account info saved');
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <div className="min-h-screen w-full">
      <div className="bg-white rounded-xl p-8 mt-7">
        <div className="flex justify-between items-center">
          <Typography variant="h5">Account info</Typography>
          <PrimaryButton
            id="save-profile-btn"
            onClick={onSave}
            className="w-[110px] h-[40px]"
            isLoading={isSaving}
          >
            Save&nbsp;changes
          </PrimaryButton>
        </div>
        <main className="flex flex-col gap-12 mt-6">
          <section>
            <TextField required label="Email" {...register('email')} />
          </section>
          <section>
            {showFeatureSettings ? (
              <>
                <Typography variant="h5" className="mb-6">
                  Features
                </Typography>
                <FeatureSettings influencer={creator} />
              </>
            ) : null}
            <Divider className="my-6" />
            <Typography variant="h5" className="mb-6">
              Custom domain
            </Typography>
            <SettingsPageCustomDomain influencerId={creator.id} />
          </section>
        </main>
      </div>
    </div>
  );
}

export default withData(CreatorAccount);
